import { useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { workflowApiRef } from '../../../api';
import { RollbackOptionsValue } from './types';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Hook for managing Cloudbuild rollback operations
 *
 */
export const useRollbackCloudbuild = () => {
  const api = useApi(workflowApiRef);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { entity } = useEntity();

  const executeRollback = useCallback(
    async ({
      projectId,
      location,
      rollbackOptions,
    }: {
      projectId: string;
      location: string;
      rollbackOptions: RollbackOptionsValue[];
    }) => {
      if (!projectId) {
        setError(new Error('No projectId provided'));
        return null;
      }

      if (!rollbackOptions || rollbackOptions.length === 0) {
        setError(new Error('No rollback options provided'));
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);

        const runIds = rollbackOptions.map(option => option.runId);
        const response = await api.reRunMultipleWorkflows({
          projectId,
          location,
          runIds,
          entityRef: stringifyEntityRef(entity),
        });

        return response;
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        } else {
          setError(err as Error);
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [api, entity],
  );

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    executeRollback,
    reset,
    location,
  };
};
